import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ShoppingBag, Store, RefreshCw, Search, Filter, Calendar,
  ChevronLeft, ChevronRight, CheckCircle, Clock, Package,
  Play, Pause, Settings, ArrowUpRight, User, DollarSign,
  Download, Copy, X, FileJson, ArrowUpDown, ArrowUp, ArrowDown,
  FileSpreadsheet, RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = '/api';

export default function ShopifyOrdersPage() {
  const [token] = useState(localStorage.getItem('token'));
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedFulfillment, setSelectedFulfillment] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Scheduler state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  
  // Selected orders for Nebim sync
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [syncingToNebim, setSyncingToNebim] = useState(false);
  
  // Nebim API Settings
  const [nebimSettings, setNebimSettings] = useState({ nebim_api_url: '', nebim_company_name: '' });
  const [showNebimSettings, setShowNebimSettings] = useState(false);
  const [savingNebimSettings, setSavingNebimSettings] = useState(false);
  
  // Nebim Export Modal
  const [nebimModalOpen, setNebimModalOpen] = useState(false);
  const [nebimExportData, setNebimExportData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [markingAsSynced, setMarkingAsSynced] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Date Filter for Pull & Sync
  const [pullStartDate, setPullStartDate] = useState('');
  const [pullEndDate, setPullEndDate] = useState('');
  
  // Sorting
  const [sortColumn, setSortColumn] = useState('created_at_shopify');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Sorted orders
  const sortedOrders = [...orders].sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];
    
    // Handle nested values
    if (sortColumn === 'customer_email') {
      aVal = a.email || a.customer?.email || '';
      bVal = b.email || b.customer?.email || '';
    }
    
    // Handle null/undefined
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    
    // Handle numeric values
    if (sortColumn === 'total_price') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    
    // Handle date values
    if (sortColumn === 'created_at_shopify') {
      aVal = new Date(aVal).getTime() || 0;
      bVal = new Date(bVal).getTime() || 0;
    }
    
    // Compare
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ token, page, limit: 50 });
      if (selectedStore) params.append('store_id', selectedStore);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedFulfillment) params.append('fulfillment', selectedFulfillment);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/shopify-orders?${params}`);
      setOrders(response.data.orders || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      setStores(response.data.stores || []);
    } catch (error) {
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [token, page, selectedStore, selectedStatus, selectedFulfillment, searchQuery]);
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/shopify-orders/stats?token=${token}`);
      setStats(response.data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  };
  
  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/shopify-orders/scheduler/status?token=${token}`);
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Scheduler status error:', error);
    }
  };
  
  useEffect(() => {
    fetchOrders();
    fetchStats();
    fetchSchedulerStatus();
    fetchNebimSettings();
  }, [fetchOrders]);
  
  const fetchNebimSettings = async () => {
    try {
      const response = await axios.get(`${API}/shopify-orders/nebim-settings?token=${token}`);
      setNebimSettings(response.data);
    } catch (error) {
      console.error('Nebim settings error:', error);
    }
  };
  
  const saveNebimSettings = async () => {
    setSavingNebimSettings(true);
    try {
      await axios.put(`${API}/shopify-orders/nebim-settings?token=${token}`, nebimSettings);
      toast.success('Nebim API ayarları kaydedildi');
      setShowNebimSettings(false);
    } catch (error) {
      toast.error('Ayarlar kaydedilemedi');
    } finally {
      setSavingNebimSettings(false);
    }
  };
  
  const startScheduler = async () => {
    setSchedulerLoading(true);
    try {
      await axios.put(`${API}/shopify-orders/scheduler/settings?token=${token}&enabled=true&pull_interval_minutes=5`);
      toast.success('Shopify sipariş servisi başlatıldı');
      fetchSchedulerStatus();
    } catch (error) {
      toast.error('Servis başlatılamadı');
    } finally {
      setSchedulerLoading(false);
    }
  };
  
  const stopScheduler = async () => {
    setSchedulerLoading(true);
    try {
      await axios.put(`${API}/shopify-orders/scheduler/settings?token=${token}&enabled=false`);
      toast.success('Shopify sipariş servisi durduruldu');
      fetchSchedulerStatus();
    } catch (error) {
      toast.error('Servis durdurulamadı');
    } finally {
      setSchedulerLoading(false);
    }
  };
  
  const runSchedulerNow = async () => {
    setSchedulerLoading(true);
    try {
      await axios.post(`${API}/shopify-orders/scheduler/run-now?token=${token}`, {}, { timeout: 300000 });
      toast.success('Siparişler çekildi');
      fetchOrders();
      fetchStats();
      fetchSchedulerStatus();
    } catch (error) {
      toast.error('Sipariş çekme hatası');
    } finally {
      setSchedulerLoading(false);
    }
  };
  
  const pullFromStore = async (storeId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/shopify-orders/pull-from-store/${storeId}?token=${token}`);
      toast.success(`${response.data.store_name}: ${response.data.new_orders} yeni sipariş çekildi`);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error('Sipariş çekme hatası');
    } finally {
      setLoading(false);
    }
  };
  
  const [nebimSyncStatus, setNebimSyncStatus] = useState(null);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  
  const _startNebimSync = async (orderIds) => {
    setSyncingToNebim(true);
    setNebimSyncStatus({ status: "running", total: orderIds.length, completed: 0, success: 0, failed: 0, current_order: "Başlatılıyor..." });
    
    try {
      const bgResp = await axios.post(
        `${API}/shopify-orders/sync-to-nebim-background?token=${token}&force=true`,
        { order_ids: orderIds },
        { timeout: 30000 }
      );
      
      if (bgResp.data.total === 0) {
        toast.info("Aktarılacak sipariş yok");
        setSyncingToNebim(false);
        setNebimSyncStatus(null);
        return;
      }
      
      toast.success(`${bgResp.data.total} sipariş 1'er 1'er aktarılıyor...`);
      
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/shopify-orders/nebim-sync-status?token=${token}`);
          const s = statusRes.data;
          setNebimSyncStatus(s);
          if (s.status === "completed" || s.status === "idle") {
            clearInterval(pollInterval);
            setSyncingToNebim(false);
            fetchOrders();
            fetchStats();
          }
        } catch (e) { }
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Aktarım hatası");
      setSyncingToNebim(false);
      setNebimSyncStatus(null);
    }
  };
  
  const syncToNebim = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Sipariş seçilmedi');
      return;
    }
    await _startNebimSync(selectedOrders);
  };
  
  // Tüm bekleyen siparişleri aktar
  const syncAllToNebim = async () => {
    setSyncingToNebim(true);
    try {
      // Tüm aktarılmamış sipariş ID'lerini topla
      let allUnsynced = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const params = new URLSearchParams({ token, limit: 200, page: page.toString() });
        const resp = await axios.get(`${API}/shopify-orders?${params}`);
        const orders = resp.data.orders || [];
        const unsynced = orders.filter(o => !o.synced_to_nebim).map(o => o.id);
        allUnsynced = [...allUnsynced, ...unsynced];
        if (orders.length < 200) hasMore = false;
        else { page++; if (page > 100) hasMore = false; }
      }
      
      if (allUnsynced.length === 0) {
        toast.info("Aktarılacak sipariş yok");
        setSyncingToNebim(false);
        return;
      }
      
      await _startNebimSync(allUnsynced);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sipariş listesi alınamadı');
      setSyncingToNebim(false);
    }
  };
  
  const markOrdersAsSynced = async () => {
    if (!nebimExportData?.order_ids?.length) return;
    
    setMarkingAsSynced(true);
    try {
      const response = await axios.post(
        `${API}/shopify-orders/mark-as-synced?token=${token}`,
        { order_ids: nebimExportData.order_ids }
      );
      
      if (response.data.success) {
        toast.success(`${response.data.updated} sipariş aktarıldı olarak işaretlendi`);
        setNebimModalOpen(false);
        setNebimExportData(null);
        setSelectedOrders([]);
        fetchOrders();
        fetchStats();
      }
    } catch (error) {
      toast.error('İşaretleme hatası');
    } finally {
      setMarkingAsSynced(false);
    }
  };
  
  // Seçili siparişlerin aktarıldı işaretini kaldır
  const unmarkSelectedOrders = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Sipariş seçilmedi');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/shopify-orders/unmark-synced?token=${token}`,
        { order_ids: selectedOrders }
      );
      
      if (response.data.success) {
        toast.success(`${response.data.updated} siparişin aktarıldı işareti kaldırıldı`);
        setSelectedOrders([]);
        fetchOrders();
        fetchStats();
      }
    } catch (error) {
      toast.error('İşaret kaldırma hatası');
    }
  };
  
  // Tüm aktarılmış siparişlerin işaretini kaldır
  const unmarkAllSyncedOrders = async () => {
    if (!window.confirm('Tüm siparişlerin aktarıldı işareti kaldırılacak. Emin misiniz?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/shopify-orders/unmark-all-synced?token=${token}`);
      
      if (response.data.success) {
        toast.success(`${response.data.updated} siparişin aktarıldı işareti kaldırıldı`);
        fetchOrders();
        fetchStats();
      }
    } catch (error) {
      toast.error('İşaret kaldırma hatası');
    }
  };
  
  // Tümünü tekrar aktar (işaretleri kaldır + aktar)
  const resyncAllOrders = async () => {
    if (!window.confirm('Tüm siparişlerin işareti kaldırılıp Nebim\'e tekrar aktarılacak. Devam etmek istiyor musunuz?')) {
      return;
    }
    
    setSyncingToNebim(true);
    try {
      // Önce tüm işaretleri kaldır
      toast.info('İşaretler kaldırılıyor...');
      await axios.post(`${API}/shopify-orders/unmark-all-synced?token=${token}`);
      
      // Sonra tümünü aktar
      toast.info('Siparişler Nebim\'e aktarılıyor...');
      const params = new URLSearchParams({ token });
      if (pullStartDate) params.append('start_date', pullStartDate);
      if (pullEndDate) params.append('end_date', pullEndDate);
      
      const response = await axios.post(
        `${API}/shopify-orders/pull-and-sync?${params}`,
        {},
        { timeout: 1200000 }
      );
      
      if (response.data.success) {
        const { synced, batches_processed } = response.data;
        toast.success(`${synced} sipariş ${batches_processed || 0} batch'te Nebim'e aktarıldı`);
        fetchOrders();
        fetchStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tekrar aktarım hatası');
    } finally {
      setSyncingToNebim(false);
    }
  };
  
  // Tüm siparişleri sil
  const deleteAllOrders = async () => {
    if (!window.confirm('Tüm çekili siparişler silinecek. Bu işlem geri alınamaz. Emin misiniz?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/shopify-orders/delete-all?token=${token}`);
      
      if (response.data.success) {
        toast.success(`${response.data.deleted} sipariş silindi`);
        fetchOrders();
        fetchStats();
      }
    } catch (error) {
      toast.error('Silme hatası');
    }
  };
  
  // Çek ve Aktar (tek işlem)
  const pullAndSyncOrders = async () => {
    setSyncingToNebim(true);
    try {
      toast.info('Siparişler çekiliyor ve aktarılıyor... Bu işlem biraz zaman alabilir.');
      
      // Build query params
      const params = new URLSearchParams({ token });
      if (pullStartDate) params.append('start_date', pullStartDate);
      if (pullEndDate) params.append('end_date', pullEndDate);
      
      const response = await axios.post(
        `${API}/shopify-orders/pull-and-sync?${params}`,
        {},
        { timeout: 1200000 } // 20 dakika timeout
      );
      
      if (response.data.success) {
        const { pulled, synced, batches_processed, pull_errors, sync_errors, customers_synced, orders_synced } = response.data;
        
        if (pulled > 0 || synced > 0 || customers_synced > 0 || orders_synced > 0) {
          toast.success(`${pulled} sipariş çekildi, ${synced} Nebim'e gönderildi, ${customers_synced || 0} müşteri, ${orders_synced || 0} sipariş aktarıldı`);
        } else {
          toast.info('Yeni sipariş bulunamadı veya aktarılacak sipariş yok');
        }
        
        if (pull_errors?.length > 0) {
          toast.error(`Çekme hataları: ${pull_errors.length}`);
        }
        if (sync_errors?.length > 0) {
          toast.error(`Aktarım hataları: ${sync_errors.length}`);
        }
        
        fetchOrders();
        fetchStats();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Çek ve aktar hatası';
      toast.error(errorMsg);
    } finally {
      setSyncingToNebim(false);
    }
  };
  
  const copyToClipboard = (data) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success('Panoya kopyalandı');
  };
  
  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('JSON indirildi');
  };
  
  // Excel'e aktar fonksiyonu - TÜM siparişleri aktar
  const exportToExcel = async () => {
    toast.info('Tüm siparişler hazırlanıyor...');
    
    try {
      // Tüm siparişleri backend'den çek
      let allOrders = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const params = new URLSearchParams({ token, page: currentPage, limit: 500 });
        if (selectedStore) params.append('store_id', selectedStore);
        if (searchQuery) params.append('search', searchQuery);
        
        const response = await axios.get(`${API}/shopify-orders?${params}`);
        const fetchedOrders = response.data.orders || [];
        allOrders = [...allOrders, ...fetchedOrders];
        
        if (fetchedOrders.length < 500) {
          hasMore = false;
        } else {
          currentPage++;
          if (currentPage > 20) hasMore = false; // Max 10000 sipariş
        }
      }
      
      if (allOrders.length === 0) {
        toast.error('Disa aktarilacak siparis yok');
        return;
      }
      
      // Türkçe karakterleri ASCII'ye çevir
      const turkishToAscii = (str) => {
        const map = {
          'ç': 'c', 'Ç': 'C', 'ğ': 'g', 'Ğ': 'G', 'ı': 'i', 'I': 'I',
          'İ': 'I', 'ö': 'o', 'Ö': 'O', 'ş': 's', 'Ş': 'S', 'ü': 'u', 'Ü': 'U'
        };
        return String(str).replace(/[çÇğĞıİöÖşŞüÜ]/g, char => map[char] || char);
      };
      
      // CSV formatında veri oluştur
      const headers = ['Magaza', 'Siparis No', 'Musteri Adi', 'E-posta', 'Tarih', 'Tutar', 'Para Birimi', 'Odeme Durumu', 'Kargo Durumu', 'Nebim Durumu'];
      const csvRows = [headers.join(';')];
      
      allOrders.forEach(order => {
        const row = [
          turkishToAscii(order.store_name || ''),
          order.order_name || '',
          turkishToAscii(`${order.customer?.first_name || ''} ${order.customer?.last_name || ''}`.trim()),
          order.customer?.email || order.email || '',
          formatDate(order.created_at_shopify),
          order.total_price || '0',
          order.currency || 'TRY',
          turkishToAscii(order.financial_status === 'paid' ? 'Odendi' : order.financial_status || ''),
          turkishToAscii(order.fulfillment_status === 'fulfilled' ? 'Gonderildi' : order.fulfillment_status || ''),
          order.synced_to_nebim ? 'Aktarildi' : 'Bekliyor'
        ];
        csvRows.push(row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'));
      });
      
      // UTF-8 BOM ile dosya oluştur
      const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const csvString = csvRows.join('\r\n');
      const encoder = new TextEncoder();
      const csvBytes = encoder.encode(csvString);
      const combined = new Uint8Array(BOM.length + csvBytes.length);
      combined.set(BOM);
      combined.set(csvBytes, BOM.length);
      
      const blob = new Blob([combined], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shopify_siparisler_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${allOrders.length} siparis Excel'e aktarildi`);
    } catch (error) {
      toast.error('Excel export hatasi');
      console.error(error);
    }
  };
  
  // Tek siparişin aktarıldı işaretini kaldır
  const unmarkSingleOrder = async (orderId) => {
    try {
      const response = await axios.post(
        `${API}/shopify-orders/unmark-synced?token=${token}`,
        { order_ids: [orderId] }
      );
      
      if (response.data.success) {
        toast.success('Siparişin aktarıldı işareti kaldırıldı');
        fetchOrders();
        fetchStats();
      }
    } catch (error) {
      toast.error('İşaret kaldırma hatası');
    }
  };
  
  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };
  
  const selectAllOrders = () => {
    const allOrderIds = orders.map(o => o.id);
    if (selectedOrders.length === allOrderIds.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(allOrderIds);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency
    }).format(parseFloat(amount || 0));
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Bekliyor', variant: 'outline', color: 'text-yellow-600' },
      paid: { label: 'Ödendi', variant: 'default', color: 'text-green-600' },
      refunded: { label: 'İade', variant: 'destructive', color: 'text-red-600' },
      partially_refunded: { label: 'Kısmi İade', variant: 'outline', color: 'text-orange-600' },
    };
    const config = statusConfig[status] || { label: status, variant: 'outline', color: '' };
    return <Badge variant={config.variant} className={config.color}>{config.label}</Badge>;
  };
  
  const getAktarimTuru = (order) => {
    const lineItems = order.line_items || [];
    const types = new Set();
    for (const item of lineItems) {
      const properties = item.properties || [];
      let isPreOrder = false;
      for (const prop of properties) {
        if (prop.name === 'Sale type' && prop.value === 'Pre-order') {
          isPreOrder = true;
          break;
        }
      }
      types.add(isPreOrder ? 'Ön Sipariş' : 'Normal');
    }
    if (types.size === 0) return ['Normal'];
    return Array.from(types);
  };

  const getFulfillmentBadge = (status) => {
    const statusConfig = {
      unfulfilled: { label: 'Hazırlanacak', variant: 'outline', color: 'bg-yellow-100 text-yellow-700' },
      fulfilled: { label: 'Gönderildi', variant: 'default', color: 'bg-green-100 text-green-700' },
      partial: { label: 'Kısmi', variant: 'outline', color: 'bg-blue-100 text-blue-700' },
    };
    const config = statusConfig[status] || { label: status, variant: 'outline', color: '' };
    return <Badge variant={config.variant} className={config.color}>{config.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingBag className="w-7 h-7 text-green-600" />
            Shopify Siparişleri
          </h1>
          <p className="text-slate-500">Tüm mağazalarınızdan siparişleri görüntüleyin ve Nebim'e aktarın</p>
        </div>
        
        <div className="flex gap-2 flex-wrap items-end">
          {/* Date Filters for Pull & Sync */}
          <div className="flex gap-2 items-end">
            <div>
              <Label className="text-xs text-slate-500">Başlangıç</Label>
              <Input 
                type="date" 
                value={pullStartDate}
                onChange={(e) => setPullStartDate(e.target.value)}
                className="w-36"
                data-testid="input-start-date"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Bitiş</Label>
              <Input 
                type="date" 
                value={pullEndDate}
                onChange={(e) => setPullEndDate(e.target.value)}
                className="w-36"
                data-testid="input-end-date"
              />
            </div>
          </div>
          
          <Button 
            variant="outline"
            onClick={exportToExcel}
            className="text-green-600 border-green-200 hover:bg-green-50"
            data-testid="btn-export-excel"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel'e Aktar
          </Button>
          <Button 
            variant="outline"
            onClick={resyncAllOrders}
            disabled={syncingToNebim}
            className="text-orange-600 border-orange-200 hover:bg-orange-50"
            data-testid="btn-resync-all"
          >
            {syncingToNebim ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4 mr-2" />
            )}
            Tümünü Tekrar Aktar
          </Button>
          <Button 
            variant="outline"
            onClick={deleteAllOrders}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="btn-delete-all-orders"
          >
            <X className="w-4 h-4 mr-2" />
            Siparişleri Sil
          </Button>
          <Button 
            onClick={pullAndSyncOrders}
            disabled={syncingToNebim}
            className="bg-green-600 hover:bg-green-700"
            data-testid="btn-pull-and-sync"
          >
            {syncingToNebim ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowUpRight className="w-4 h-4 mr-2" />
            )}
            Çek ve Aktar
          </Button>
          <Button 
            variant="outline" 
            onClick={() => { fetchOrders(); fetchStats(); }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
        </div>
      </div>
      
      {/* Nebim Aktarım İlerleme Paneli */}
      {nebimSyncStatus && (nebimSyncStatus.status === "running" || nebimSyncStatus.status === "completed") && (
        <Card className={nebimSyncStatus.status === "completed" ? "border-emerald-300 bg-emerald-50/50" : "border-orange-300 bg-orange-50/50"}>
          <CardContent className="py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {nebimSyncStatus.status === "running" ? (
                    <RefreshCw className="w-5 h-5 text-orange-600 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  )}
                  <span className="font-semibold text-sm">
                    {nebimSyncStatus.status === "running" ? "Nebim'e Aktarılıyor..." : "Aktarım Tamamlandı"}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-emerald-700 font-medium">{nebimSyncStatus.success || 0} Başarılı</span>
                  {(nebimSyncStatus.failed || 0) > 0 && (
                    <span className="text-red-600 font-medium">{nebimSyncStatus.failed} Hatalı</span>
                  )}
                  {(nebimSyncStatus.customers || 0) > 0 && (
                    <span className="text-blue-600 font-medium">{nebimSyncStatus.customers} Müşteri</span>
                  )}
                  <span className="text-slate-500">{nebimSyncStatus.completed || 0}/{nebimSyncStatus.total || 0}</span>
                  {nebimSyncStatus.status === "completed" && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setNebimSyncStatus(null)}>Kapat</Button>
                  )}
                </div>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2.5">
                <div 
                  className={`h-2.5 rounded-full transition-all duration-500 ${nebimSyncStatus.status === "completed" ? "bg-emerald-500" : "bg-orange-500"}`}
                  style={{ width: `${nebimSyncStatus.total > 0 ? Math.round(((nebimSyncStatus.completed || 0) / nebimSyncStatus.total) * 100) : 0}%` }}
                />
              </div>
              {nebimSyncStatus.status === "running" && nebimSyncStatus.current_order && (
                <p className="text-xs text-slate-500 truncate">Aktarılıyor: <span className="text-slate-700">{nebimSyncStatus.current_order}</span></p>
              )}
              {nebimSyncStatus.status === "completed" && nebimSyncStatus.error_list && nebimSyncStatus.error_list.length > 0 && (
                <div className="mt-2 border border-red-200 rounded-lg bg-white p-3 max-h-60 overflow-y-auto">
                  <p className="text-xs font-semibold text-red-600 mb-2">Hatalı Siparişler ({nebimSyncStatus.error_list.length}):</p>
                  {nebimSyncStatus.error_list.map((e, i) => (
                    <div key={i} className="text-xs py-1.5 border-b border-red-50 last:border-0">
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-slate-700 whitespace-nowrap">{e.order}</span>
                        {e.step && <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 text-orange-600 border-orange-200">{e.step}</Badge>}
                      </div>
                      <p className="text-red-500 mt-0.5">{e.error}</p>
                      {e.steps && e.steps.length > 0 && (
                        <details className="mt-1">
                          <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600">Adımlar ({e.steps.length})</summary>
                          <div className="mt-1 pl-2 border-l-2 border-slate-200 space-y-0.5">
                            {e.steps.map((step, si) => (
                              <p key={si} className="text-[10px] text-slate-500 font-mono">{step}</p>
                            ))}
                          </div>
                        </details>
                      )}
                      {e.nebim_response && (
                        <details className="mt-1">
                          <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600">Nebim Yanıtı</summary>
                          <pre className="mt-1 text-[10px] bg-slate-50 p-1.5 rounded font-mono text-slate-600 whitespace-pre-wrap break-all max-h-24 overflow-y-auto">{e.nebim_response}</pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Toplam Sipariş</p>
                <p className="text-2xl font-bold">{stats?.total_orders || 0}</p>
              </div>
              <ShoppingBag className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Nebim'e Aktarılan</p>
                <p className="text-2xl font-bold text-green-600">{stats?.synced_to_nebim || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
            </div>
            {(stats?.synced_to_nebim || 0) > 0 && (
              <Button 
                onClick={unmarkAllSyncedOrders}
                variant="outline"
                className="w-full mt-3 text-red-600 border-red-200 hover:bg-red-50"
                size="sm"
                data-testid="btn-unmark-all-synced"
              >
                <X className="w-4 h-4 mr-2" />
                Tüm İşaretleri Kaldır
              </Button>
            )}
          </CardContent>
        </Card>
        
        <Card className="border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Bekleyen</p>
                <p className="text-2xl font-bold text-orange-600">{stats?.pending_nebim || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
            {(stats?.pending_nebim || 0) > 0 && (
              <Button 
                onClick={syncAllToNebim}
                disabled={syncingToNebim}
                className="w-full mt-3 bg-orange-600 hover:bg-orange-700"
                size="sm"
                data-testid="btn-sync-all-to-nebim"
              >
                {syncingToNebim ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                )}
                Tümünü Aktar
              </Button>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Mağaza Sayısı</p>
                <p className="text-2xl font-bold">{stores.length}</p>
              </div>
              <Store className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Mağaza</Label>
              <Select value={selectedStore || "all"} onValueChange={(val) => setSelectedStore(val === "all" ? "" : val)}>
                <SelectTrigger data-testid="filter-store">
                  <SelectValue placeholder="Tüm Mağazalar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Mağazalar</SelectItem>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.store_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[150px]">
              <Label>Ödeme Durumu</Label>
              <Select value={selectedStatus || "all"} onValueChange={(val) => setSelectedStatus(val === "all" ? "" : val)}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="pending">Bekliyor</SelectItem>
                  <SelectItem value="paid">Ödendi</SelectItem>
                  <SelectItem value="refunded">İade</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[150px]">
              <Label>Kargo Durumu</Label>
              <Select value={selectedFulfillment || "all"} onValueChange={(val) => setSelectedFulfillment(val === "all" ? "" : val)}>
                <SelectTrigger data-testid="filter-fulfillment">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="unfulfilled">Hazırlanacak</SelectItem>
                  <SelectItem value="fulfilled">Gönderildi</SelectItem>
                  <SelectItem value="partial">Kısmi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[250px]">
              <Label>Ara</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Sipariş no, müşteri..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <Button variant="outline" onClick={() => { setSelectedStore(''); setSelectedStatus(''); setSelectedFulfillment(''); setSearchQuery(''); }}>
              <Filter className="w-4 h-4 mr-2" />
              Temizle
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Nebim Sync Action */}
      {selectedOrders.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <p className="font-medium">
                {selectedOrders.length} sipariş seçildi
              </p>
              <div className="flex gap-2">
                <Button 
                  onClick={unmarkSelectedOrders}
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="btn-unmark-selected"
                >
                  <X className="w-4 h-4 mr-2" />
                  İşareti Kaldır
                </Button>
                <Button 
                  onClick={syncToNebim}
                  disabled={syncingToNebim}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="btn-sync-to-nebim"
                >
                  {syncingToNebim ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                  )}
                  Nebim'e Aktar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Orders Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Sipariş bulunamadı</p>
              <p className="text-sm">Mağazalarınızdan sipariş çekmek için servisi başlatın</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 pl-2">
                        <Checkbox 
                          checked={selectedOrders.length === orders.filter(o => !o.synced_to_nebim).length && selectedOrders.length > 0}
                          onCheckedChange={selectAllOrders}
                        />
                      </th>
                      <th className="pb-3 cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort('store_name')}>
                        <div className="flex items-center gap-1">
                          Mağaza
                          {sortColumn === 'store_name' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                        </div>
                      </th>
                      <th className="pb-3 cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort('order_name')}>
                        <div className="flex items-center gap-1">
                          Sipariş No
                          {sortColumn === 'order_name' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                        </div>
                      </th>
                      <th className="pb-3 cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort('customer_email')}>
                        <div className="flex items-center gap-1">
                          Müşteri
                          {sortColumn === 'customer_email' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                        </div>
                      </th>
                      <th className="pb-3 cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort('created_at_shopify')}>
                        <div className="flex items-center gap-1">
                          Tarih
                          {sortColumn === 'created_at_shopify' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                        </div>
                      </th>
                      <th className="pb-3 cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort('total_price')}>
                        <div className="flex items-center gap-1">
                          Tutar
                          {sortColumn === 'total_price' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                        </div>
                      </th>
                      <th className="pb-3 cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort('financial_status')}>
                        <div className="flex items-center gap-1">
                          Ödeme
                          {sortColumn === 'financial_status' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                        </div>
                      </th>
                      <th className="pb-3 cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort('fulfillment_status')}>
                        <div className="flex items-center gap-1">
                          Kargo
                          {sortColumn === 'fulfillment_status' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                        </div>
                      </th>
                      <th className="pb-3" data-testid="th-aktarim-turu">
                        <div className="flex items-center gap-1">
                          Aktarım Türü
                        </div>
                      </th>
                      <th className="pb-3 cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort('synced_to_nebim')}>
                        <div className="flex items-center gap-1">
                          Nebim
                          {sortColumn === 'synced_to_nebim' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOrders.map(order => (
                      <tr key={order.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedOrderDetail(order)}>
                        <td className="py-3 pl-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={selectedOrders.includes(order.id)}
                            onCheckedChange={() => toggleOrderSelection(order.id)}
                          />
                        </td>
                        <td className="py-3">
                          <Badge variant="outline" className="font-normal">
                            {order.store_name}
                          </Badge>
                        </td>
                        <td className="py-3 font-medium">{order.order_name}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <div>
                              <p className="text-sm">{order.customer?.first_name} {order.customer?.last_name}</p>
                              <p className="text-xs text-slate-500">{order.customer?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 text-sm">{formatDate(order.created_at_shopify)}</td>
                        <td className="py-3 font-medium">{formatCurrency(order.total_price, order.currency)}</td>
                        <td className="py-3">{getStatusBadge(order.financial_status)}</td>
                        <td className="py-3">{getFulfillmentBadge(order.fulfillment_status)}</td>
                        <td className="py-3" data-testid={`aktarim-turu-${order.id}`}>
                          <div className="flex flex-wrap gap-1">
                            {getAktarimTuru(order).map((type, i) => (
                              <Badge key={i} variant="outline" className={type === 'Ön Sipariş' ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                                {type}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="py-3">
                          {order.synced_to_nebim ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="default" className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {order.nebim_order_number ? `#${order.nebim_order_number}` : "Aktarıldı"}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => unmarkSingleOrder(order.id)}
                                className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                                title="İşareti kaldır ve tekrar aktarabilirsin"
                                data-testid={`btn-unmark-${order.id}`}
                              >
                                <RotateCcw className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <Badge variant="outline" className="text-orange-600 w-fit">
                                Bekliyor
                              </Badge>
                              {order.nebim_last_error && (
                                <p className="text-[10px] text-red-400 max-w-[180px] truncate" title={order.nebim_last_error}>
                                  {order.nebim_last_error.substring(0, 60)}...
                                </p>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-slate-500">
                  Toplam {total} sipariş
                </p>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">
                    Sayfa {page} / {totalPages}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Store Cards - Quick Pull */}
      {stores.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Mağazalardan Hızlı Çekme</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stores.map(store => {
              const storeStat = stats?.store_stats?.find(s => s._id === store.id);
              return (
                <Card key={store.id} className="hover:border-green-300 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{store.store_name}</h3>
                        <p className="text-xs text-slate-500">{storeStat?.total_orders || 0} sipariş</p>
                      </div>
                      <Store className="w-5 h-5 text-green-500" />
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => pullFromStore(store.id)}
                      disabled={loading}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Siparişleri Çek
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Nebim Export Modal */}
      <Dialog open={nebimModalOpen} onOpenChange={setNebimModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileJson className="w-5 h-5 text-blue-600" />
              Nebim JSON Export
            </DialogTitle>
            <DialogDescription>
              {nebimExportData?.counts && (
                <span>
                  {nebimExportData.counts.orders} sipariş, {nebimExportData.counts.items} ürün satırı
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="all" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="all">Tümü</TabsTrigger>
              <TabsTrigger value="orders">Siparişler</TabsTrigger>
              <TabsTrigger value="items">Ürünler</TabsTrigger>
              <TabsTrigger value="billing">Fatura</TabsTrigger>
              <TabsTrigger value="shipping">Kargo</TabsTrigger>
            </TabsList>
            
            <div className="flex-1 overflow-auto mt-4 border rounded-lg bg-slate-900 p-4">
              <TabsContent value="all" className="mt-0 h-full">
                <pre className="text-xs text-green-400 whitespace-pre-wrap">
                  {nebimExportData?.tables && JSON.stringify(nebimExportData.tables, null, 2)}
                </pre>
              </TabsContent>
              <TabsContent value="orders" className="mt-0 h-full">
                <pre className="text-xs text-green-400 whitespace-pre-wrap">
                  {nebimExportData?.tables?.ZTMSG_ShopifyOrders && JSON.stringify(nebimExportData.tables.ZTMSG_ShopifyOrders, null, 2)}
                </pre>
              </TabsContent>
              <TabsContent value="items" className="mt-0 h-full">
                <pre className="text-xs text-green-400 whitespace-pre-wrap">
                  {nebimExportData?.tables?.ZTMSG_ShopifyOrderItems && JSON.stringify(nebimExportData.tables.ZTMSG_ShopifyOrderItems, null, 2)}
                </pre>
              </TabsContent>
              <TabsContent value="billing" className="mt-0 h-full">
                <pre className="text-xs text-green-400 whitespace-pre-wrap">
                  {nebimExportData?.tables?.ZTMSG_ShopifyOrderBilling && JSON.stringify(nebimExportData.tables.ZTMSG_ShopifyOrderBilling, null, 2)}
                </pre>
              </TabsContent>
              <TabsContent value="shipping" className="mt-0 h-full">
                <pre className="text-xs text-green-400 whitespace-pre-wrap">
                  {nebimExportData?.tables?.ZTMSG_ShopifyOrderShipping && JSON.stringify(nebimExportData.tables.ZTMSG_ShopifyOrderShipping, null, 2)}
                </pre>
              </TabsContent>
            </div>
          </Tabs>
          
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4 border-t">
            <div className="flex gap-2 flex-1">
              <Button 
                variant="outline" 
                onClick={() => copyToClipboard(nebimExportData?.tables)}
                data-testid="btn-copy-json"
              >
                <Copy className="w-4 h-4 mr-2" />
                Kopyala
              </Button>
              <Button 
                variant="outline"
                onClick={() => downloadJson(nebimExportData?.tables, `nebim_export_${Date.now()}.json`)}
                data-testid="btn-download-json"
              >
                <Download className="w-4 h-4 mr-2" />
                İndir
              </Button>
            </div>
            <Button 
              onClick={markOrdersAsSynced}
              disabled={markingAsSynced}
              className="bg-green-600 hover:bg-green-700"
              data-testid="btn-mark-as-synced"
            >
              {markingAsSynced ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Aktarıldı Olarak İşaretle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sipariş Detay Modal */}
      <Dialog open={!!selectedOrderDetail} onOpenChange={() => setSelectedOrderDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedOrderDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedOrderDetail.order_name}</span>
                  <div className="flex items-center gap-2">
                    {selectedOrderDetail.synced_to_nebim && selectedOrderDetail.nebim_order_number && (
                      <Badge className="bg-green-100 text-green-700">Nebim #{selectedOrderDetail.nebim_order_number}</Badge>
                    )}
                    {getStatusBadge(selectedOrderDetail.financial_status)}
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Genel Bilgiler */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <p className="text-slate-500">Mağaza</p>
                    <p className="font-medium">{selectedOrderDetail.store_name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-slate-500">Tarih</p>
                    <p className="font-medium">{formatDate(selectedOrderDetail.created_at_shopify)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-slate-500">Müşteri</p>
                    <p className="font-medium">{selectedOrderDetail.customer?.first_name} {selectedOrderDetail.customer?.last_name}</p>
                    <p className="text-xs text-slate-400">{selectedOrderDetail.customer?.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-slate-500">Telefon</p>
                    <p className="font-medium">{selectedOrderDetail.phone || selectedOrderDetail.shipping_address?.phone || "-"}</p>
                  </div>
                </div>

                {/* Ürünler Tablosu */}
                <div>
                  <h3 className="font-semibold text-sm mb-2">Ürünler ({selectedOrderDetail.line_items?.length || 0})</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2 font-medium text-slate-600">#</th>
                          <th className="text-left p-2 font-medium text-slate-600">Ürün</th>
                          <th className="text-left p-2 font-medium text-slate-600">SKU</th>
                          <th className="text-left p-2 font-medium text-slate-600">Varyant</th>
                          <th className="text-right p-2 font-medium text-slate-600">Fiyat</th>
                          <th className="text-right p-2 font-medium text-slate-600">Adet</th>
                          <th className="text-right p-2 font-medium text-slate-600">İndirim</th>
                          <th className="text-right p-2 font-medium text-slate-600">Toplam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedOrderDetail.line_items || []).map((item, i) => {
                          const discount = (item.discount_allocations || []).reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
                          const lineTotal = parseFloat(item.price || 0) * parseInt(item.quantity || 1) - discount;
                          return (
                            <tr key={i} className="border-t">
                              <td className="p-2 text-slate-400">{i + 1}</td>
                              <td className="p-2 font-medium">{item.title}</td>
                              <td className="p-2 font-mono text-xs">{item.sku || "-"}</td>
                              <td className="p-2 text-slate-600">{item.variant_title || "-"}</td>
                              <td className="p-2 text-right">{formatCurrency(item.price, selectedOrderDetail.currency)}</td>
                              <td className="p-2 text-right font-medium">{item.quantity}</td>
                              <td className="p-2 text-right text-red-500">{discount > 0 ? `-${formatCurrency(discount, selectedOrderDetail.currency)}` : "-"}</td>
                              <td className="p-2 text-right font-medium">{formatCurrency(lineTotal, selectedOrderDetail.currency)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-50 font-medium">
                        <tr className="border-t">
                          <td colSpan={4} className="p-2">Ara Toplam</td>
                          <td colSpan={4} className="p-2 text-right">{formatCurrency(selectedOrderDetail.subtotal_price, selectedOrderDetail.currency)}</td>
                        </tr>
                        {parseFloat(selectedOrderDetail.total_discounts || 0) > 0 && (
                          <tr className="border-t text-red-600">
                            <td colSpan={4} className="p-2">İndirim</td>
                            <td colSpan={4} className="p-2 text-right">-{formatCurrency(selectedOrderDetail.total_discounts, selectedOrderDetail.currency)}</td>
                          </tr>
                        )}
                        {parseFloat(selectedOrderDetail.total_shipping_price || 0) > 0 && (
                          <tr className="border-t">
                            <td colSpan={4} className="p-2">Kargo</td>
                            <td colSpan={4} className="p-2 text-right">{formatCurrency(selectedOrderDetail.total_shipping_price, selectedOrderDetail.currency)}</td>
                          </tr>
                        )}
                        <tr className="border-t text-base">
                          <td colSpan={4} className="p-2 font-bold">Genel Toplam</td>
                          <td colSpan={4} className="p-2 text-right font-bold">{formatCurrency(selectedOrderDetail.total_price, selectedOrderDetail.currency)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Adres Bilgileri */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedOrderDetail.shipping_address && (
                    <div className="border rounded-lg p-3">
                      <h4 className="font-semibold text-xs text-slate-500 mb-2">TESLİMAT ADRESİ</h4>
                      <p className="text-sm font-medium">{selectedOrderDetail.shipping_address.first_name} {selectedOrderDetail.shipping_address.last_name}</p>
                      {selectedOrderDetail.shipping_address.company && <p className="text-sm text-slate-600">{selectedOrderDetail.shipping_address.company}</p>}
                      <p className="text-sm text-slate-600">{selectedOrderDetail.shipping_address.address1}</p>
                      {selectedOrderDetail.shipping_address.address2 && <p className="text-sm text-slate-600">{selectedOrderDetail.shipping_address.address2}</p>}
                      <p className="text-sm text-slate-600">{selectedOrderDetail.shipping_address.city} {selectedOrderDetail.shipping_address.province} {selectedOrderDetail.shipping_address.zip}</p>
                      <p className="text-sm text-slate-600">{selectedOrderDetail.shipping_address.country}</p>
                      {selectedOrderDetail.shipping_address.phone && <p className="text-sm text-slate-500 mt-1">{selectedOrderDetail.shipping_address.phone}</p>}
                    </div>
                  )}
                  {selectedOrderDetail.billing_address && (
                    <div className="border rounded-lg p-3">
                      <h4 className="font-semibold text-xs text-slate-500 mb-2">FATURA ADRESİ</h4>
                      <p className="text-sm font-medium">{selectedOrderDetail.billing_address.first_name} {selectedOrderDetail.billing_address.last_name}</p>
                      {selectedOrderDetail.billing_address.company && <p className="text-sm text-slate-600">{selectedOrderDetail.billing_address.company}</p>}
                      <p className="text-sm text-slate-600">{selectedOrderDetail.billing_address.address1}</p>
                      <p className="text-sm text-slate-600">{selectedOrderDetail.billing_address.city} {selectedOrderDetail.billing_address.zip}</p>
                      <p className="text-sm text-slate-600">{selectedOrderDetail.billing_address.country}</p>
                    </div>
                  )}
                </div>

                {/* Metafields */}
                {selectedOrderDetail.metafields && selectedOrderDetail.metafields.length > 0 && (
                  <div className="border rounded-lg p-3">
                    <h4 className="font-semibold text-xs text-slate-500 mb-2">METAFIELDS</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedOrderDetail.metafields.map((mf, i) => (
                        <div key={i} className="text-sm">
                          <span className="text-slate-500">{mf.key}:</span> <span className="font-medium">{mf.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nebim Aktarım Logu */}
                {(selectedOrderDetail.nebim_last_error || selectedOrderDetail.nebim_sync_log) && (
                  <div className={`border rounded-lg p-3 ${selectedOrderDetail.nebim_last_error ? 'border-red-200 bg-red-50/50' : 'border-green-200 bg-green-50/50'}`}>
                    <h4 className="font-semibold text-xs text-slate-500 mb-2">NEBİM AKTARIM LOGU</h4>
                    
                    {selectedOrderDetail.nebim_last_error && (
                      <div className="mb-2">
                        <p className="text-xs font-medium text-red-600">Son Hata:</p>
                        <p className="text-xs text-red-500 mt-0.5">{selectedOrderDetail.nebim_last_error}</p>
                      </div>
                    )}
                    
                    {selectedOrderDetail.nebim_last_attempt && (
                      <p className="text-xs text-slate-400 mb-2">Son Deneme: {formatDate(selectedOrderDetail.nebim_last_attempt)}</p>
                    )}
                    
                    {selectedOrderDetail.nebim_sync_log?.steps && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 mb-1">İşlem Adımları:</p>
                        <div className="space-y-0.5 pl-2 border-l-2 border-slate-200">
                          {selectedOrderDetail.nebim_sync_log.steps.map((step, si) => (
                            <p key={si} className={`text-[11px] font-mono ${step.includes('OK') || step.includes('✅') ? 'text-green-600' : step.includes('HATA') || step.includes('HTTP 4') || step.includes('HTTP 5') ? 'text-red-500' : 'text-slate-500'}`}>{step}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {selectedOrderDetail.nebim_sync_log?.nebim_responses && Object.keys(selectedOrderDetail.nebim_sync_log.nebim_responses).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">Nebim API Yanıtları</summary>
                        <pre className="mt-1 text-[10px] bg-white p-2 rounded border font-mono text-slate-600 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                          {JSON.stringify(selectedOrderDetail.nebim_sync_log.nebim_responses, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
